import { teleTools, tools } from "./data";

function landingPageImage(filename: string) {
  return `/landing-page/${encodeURIComponent(filename)}`;
}

function toolById(id: (typeof tools)[number]["id"]) {
  const t = tools.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown tool id: ${id}`);
  return t;
}

export type FrontGuideStep = { title: string; body: string };

export const activationSteps: FrontGuideStep[] = [
  {
    title: "Step 1: Check your email",
    body: "Check your inbox for the activation code email. If you don't see it in your inbox, please make sure to check your spam folder.",
  },
  {
    title: "Step 2: Copy Activation Code",
    body: "Once you have located your activation code, find the Activation Code field (e.g., FNEV...) in your subscription panel and click the copy icon next to it.",
  },
  {
    title: "Step 3: Open extension and paste the code",
    body: 'Open the extension and click on "Already Purchased? Enter Code." Paste the code into the activation field and click Activate. Your subscription is now successfully activated. Enjoy using your extension!',
  },
];

export const activationFallbackSteps: FrontGuideStep[] = [
  {
    title: "Step 1: Go to Login & Manage",
    body: 'Click on the Login & Manage tab within the dashboard. Locate your extension card and click "Login & Manage." Enter your payment email and click "Send OTP."',
  },
  {
    title: "Step 2: Open your email",
    body: "Check your inbox for the OTP and copy it. If it isn't there, please check your spam folder.",
  },
  {
    title: "Step 3: Paste the OTP",
    body: "Paste the OTP into the field and click Submit.",
  },
  {
    title: "Step 4: Copy Activation Code",
    body: "Once logged in, locate the Activation Code field (e.g., FNEV...) in the subscription panel and click the copy icon next to it.",
  },
  {
    title: "Step 5: Open extension and paste the code",
    body: 'Open the extension. Click on "Already Purchased? Enter Code." Paste the code into the activation field and click Activate. Your subscription is now successfully activated. Enjoy using your extension!',
  },
];

export const unsubscribeSteps: FrontGuideStep[] = [
  {
    title: "Step 1: Go to Login & Manage",
    body: "Click on the Login & Manage tab within the dashboard.",
  },
  {
    title: "Step 2: Choose your extension",
    body: 'Locate your extension card and click "Login & Manage." Enter your payment email and click "Send OTP."',
  },
  {
    title: "Step 3: Open your email",
    body: "Check your inbox for the OTP and copy it.",
  },
  {
    title: "Step 4: Paste the OTP",
    body: "Paste the OTP into the field and click Submit.",
  },
  {
    title: "Step 5: Click on Cancel button",
    body: 'Click the "Cancel" button at the top right of your subscription details to stop auto-renewal. Your subscription will then be canceled.',
  },
];

export type FrontProductFeature = { bold: string; text: string };

export type FrontProduct = {
  tag: string;
  title: string;
  description: string;
  features: FrontProductFeature[];
  image: string;
  installUrl: string;
  tutorialUrl: string;
  platform: "tg";
};

const tgb = toolById("tgb");
const tgv = toolById("tgv");
const tgt = toolById("tgt");
const [tgPrivacy] = teleTools;

export const tgExploreProducts: FrontProduct[] = [
  {
    tag: "Marketing",
    title: "Telegram Sender Pro",
    description:
      "Mass message sender to easily send unlimited texts and automate services.",
    features: [
      { bold: "Excel Upload:", text: " Import numbers manually." },
      { bold: "Drafting:", text: " Supports Text, Image, or Doc." },
      { bold: "Automated Blast:", text: " Efficient mass messaging." },
    ],
    image: landingPageImage("Telegram Bulk Sender.png"),
    installUrl: tgb.extensionUrl,
    tutorialUrl: "https://youtu.be/_N7-iFk5cRk?si=M8lca7MqSSgQ6jP0",
    platform: "tg",
  },
  {
    tag: "Media",
    title: "Telegram Video Downloader",
    description:
      "Download videos, images, and private media directly from Telegram Web.",
    features: [
      { bold: "One-Click Access:", text: " Real-time progress tracking." },
      { bold: "Batch Download:", text: " Download multiple files easily." },
      { bold: "Secure Extension:", text: " Easy, lightweight, and safe." },
    ],
    image: landingPageImage("Telegram Video Downloader.png"),
    installUrl: tgv.extensionUrl,
    tutorialUrl: "https://youtu.be/jDFMOBT8n2k?si=8E7rlp7xI7inA6uD",
    platform: "tg",
  },
  {
    tag: "Global",
    title: "Telegram Web Translator",
    description:
      "Translate messages in one click or enable auto-translation for seamless chats.",
    features: [
      { bold: "Instant Detect:", text: " Accurate auto-detection." },
      { bold: "Effortless Replies:", text: " Reply in any language." },
      { bold: "Truly Global:", text: " 100+ languages supported." },
    ],
    image: landingPageImage("Telegram Translator.png"),
    installUrl: tgt.extensionUrl,
    tutorialUrl: "https://youtu.be/Mid0uRvln0s?si=EQDXlFf8s1-cCvtg",
    platform: "tg",
  },
  {
    tag: "Privacy",
    title: "Telegram Privacy Extension",
    description:
      "Protect your conversations by blurring sensitive information on your screen.",
    features: [
      { bold: "Toggle All:", text: " Blur chats, names, and media." },
      { bold: "Selective Adjust:", text: " Use the intensity slider." },
      { bold: "Custom Settings:", text: " Choose exactly what to blur." },
    ],
    image: landingPageImage("Telegram  Privacy.png"),
    installUrl: tgPrivacy.url,
    tutorialUrl: "https://www.youtube.com/watch?v=OXeTc3IT_RU",
    platform: "tg",
  },
];

export const frontPageGifs = {
  activation: "/landing-page/Activation.gif",
  unsubscribe: "/landing-page/Unsubscribe.gif",
} as const;
